<script>
import { mapGetters } from 'vuex';
import { debounce } from '@chatwoot/utils';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import {
  cannedQueryCovers,
  cannedResponseMatches,
} from 'dashboard/helper/cannedResponseHelper';
import MentionBox from '../mentions/MentionBox.vue';

export default {
  components: { MentionBox },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
  },
  emits: ['replace'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return { getPlainText };
  },
  data() {
    return {
      expandedGroupKey: null,
      previewResponse: null,
    };
  },
  computed: {
    ...mapGetters({
      groupedResponses: 'getGroupedCannedResponses',
      groupedQuery: 'getGroupedCannedQuery',
    }),
    // Only results fetched for this term, or for a shorter prefix of it (filtered
    // locally), are shown, so a previous command cannot stay on screen.
    matchedGroups() {
      const term = this.searchKey || '';
      if (!cannedQueryCovers(this.groupedQuery, term)) return [];

      return this.groupedResponses.filter(group =>
        cannedResponseMatches(group.short_code, term)
      );
    },
    items() {
      return this.matchedGroups.map(group => ({
        label: group.short_code,
        key: `${group.short_code}:${group.primary?.id}`,
        group,
      }));
    },
  },
  watch: {
    searchKey() {
      this.debouncedFetch();
    },
  },
  created() {
    this.searchController = null;
    this.debouncedFetch = debounce(() => this.fetchCannedResponses(), 300);
  },
  mounted() {
    this.fetchCannedResponses();
  },
  beforeUnmount() {
    this.searchController?.abort();
  },
  methods: {
    fetchCannedResponses() {
      this.searchController?.abort();
      this.searchController = new AbortController();
      this.$store.dispatch('getGroupedCannedResponse', {
        searchKey: this.searchKey,
        signal: this.searchController.signal,
      });
    },
    toEmitPayload(cannedResponse) {
      return {
        label: cannedResponse.short_code,
        key: cannedResponse.short_code,
        description: cannedResponse.content,
        labelIds: (cannedResponse.label_ids || [])
          .map(Number)
          .filter(id => Number.isFinite(id) && id > 0),
        labelTitles: cannedResponse.label_titles || [],
      };
    },
    handleMentionClick(item = {}) {
      // Enter/клик по строке — используем самый приоритетный (primary).
      this.$emit('replace', this.toEmitPayload(item.group.primary));
    },
    toggleExpand(shortCode, event) {
      event.stopPropagation();
      this.expandedGroupKey =
        this.expandedGroupKey === shortCode ? null : shortCode;
    },
    selectAlternate(cannedResponse, event) {
      event.stopPropagation();
      this.$emit('replace', this.toEmitPayload(cannedResponse));
      this.expandedGroupKey = null;
      this.previewResponse = null;
    },
    onSelectionChange() {
      this.previewResponse = null;
    },
    onPreviewHover(cannedResponse) {
      this.previewResponse = cannedResponse;
    },
    onPreviewReset() {
      this.previewResponse = null;
    },
    previewForItem(item) {
      if (
        this.previewResponse &&
        this.previewResponse.short_code === item.group.short_code
      ) {
        return this.previewResponse;
      }

      return item.group.primary;
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <MentionBox
    v-if="items.length"
    :items="items"
    size="lg"
    @mention-select="handleMentionClick"
    @selection-change="onSelectionChange"
  >
    <template #default="{ item, selected }">
      <div class="flex items-center justify-between w-full gap-2">
        <p
          class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
          :class="{ 'text-n-slate-12': selected }"
        >
          {{ item.label }}
        </p>
        <p
          class="shrink-0 mb-0 text-xs text-n-slate-11 group-hover:text-n-slate-12"
          :class="{ 'text-n-slate-12': selected }"
        >
          {{ item.group.primary.scope_label }}
        </p>
      </div>
    </template>

    <template #extra="{ item }">
      <div v-if="item.group.duplicate_count > 1" class="pl-3">
        <button
          type="button"
          class="text-xs text-n-slate-11 hover:text-n-slate-12 py-1 flex items-center gap-1"
          @click="toggleExpand(item.group.short_code, $event)"
        >
          <span
            class="i-ph-caret-right size-3 transition-transform"
            :class="{ 'rotate-90': expandedGroupKey === item.group.short_code }"
          />
          {{
            $t(
              'CANNED_MGMT.MENTION.VARIANTS_COUNT',
              item.group.duplicate_count,
              { count: item.group.duplicate_count }
            )
          }}
        </button>

        <div v-if="expandedGroupKey === item.group.short_code" class="pb-1">
          <button
            v-for="alt in item.group.alternates"
            :key="alt.id"
            type="button"
            class="w-full text-left px-2 py-1.5 rounded-md hover:bg-n-alpha-black2 flex items-center justify-between"
            @mouseenter="onPreviewHover(alt)"
            @mouseleave="onPreviewReset"
            @click="selectAlternate(alt, $event)"
          >
            <span class="text-xs text-n-slate-11">{{ alt.scope_label }}</span>
          </button>
        </div>
      </div>
    </template>

    <template #preview="{ item }">
      <div class="flex flex-col gap-2 h-full">
        <div>
          <p class="mb-0 text-xs font-medium text-n-slate-12">
            /{{ item.label }}
          </p>
          <p
            v-if="previewForItem(item).scope_label"
            class="mb-0 mt-1 text-xs text-n-slate-11"
          >
            {{ previewForItem(item).scope_label }}
          </p>
        </div>
        <p class="mb-0 text-sm text-n-slate-11 whitespace-pre-wrap break-words">
          {{ getPlainText(previewForItem(item).content) }}
        </p>
      </div>
    </template>
  </MentionBox>
</template>
